import { useCallback, useEffect, useState, memo } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { makeStyles } from '@material-ui/core';
import PageContainer from '../../shared/pageContainer';
import Filter from '../../shared/filter';
import NotificationCard from '../../shared/notificationCard';
import GridTablePlain from '../../shared/gridTablePlain';
import UserAvatar from '../../shared/userAvatar';
import CustomFormDrawer from '../../shared/customFormDrawer';
import AlertDialog from '../../shared/alertDialog';
import TextBoxWithTooltip from '../../shared/textBoxWithTooltip';
import ListWithTooltip from '../../shared/listWithTooltip';
import DotsMenu from '../../shared/dotsMenu';
import Search from '../../shared/search';
import {
  api_candidate,
  api_candidates_in_jobs,
} from '../../../constants/apiRoutes';
import {
  APP_PAGES,
  CANDIDATES_DEFAULT_PARAMS,
  PAGE_WHITELISTED_PARAMS,
  PARAMS,
} from '../../../constants/pages';
import { PERMISSION_CODES } from '../../../constants/permissions';
import { sticky } from '../../../constants/helperCssRules';
import { jobsSelector } from '../../../store/selectors/jobsSelector';
import { tagsSelector } from '../../../store/selectors/tagsSelector';
import { divisionsAndDepartmentsSelector } from '../../../store/selectors/divisionsAndDepartmentsSelector';
import {
  getDivisionsAndDepartments,
  clearDivisionsAndDepartments,
} from '../../../store/modules/divisionsAndDepartments';
import {
  getAllJobs,
  getCandidates,
  clearAllJobs,
  clearCandidates,
} from '../../../store/modules/jobs';
import {
  getTagsCategories,
  clearTagsCategories,
} from '../../../store/modules/tags';
import http from '../../../utility/http';
import { useTranslations } from '../../../utility/useTranslations';
import {
  getObjectToNumberArray,
  isArray,
  isArrayEmpty,
  isObject,
  isObjectEmpty,
  trimString,
} from '../../../utility/helpers';
import {
  getPersonFullName,
  hasNextPage,
  parseDuplicateParameters,
  parseQueryParams,
  showSuccessMessage,
} from '../../../utility/uiUtils';
import { hasAuthPermissionsCheck } from '../../../utility/permissions';
import { isEmpty } from '../../../utility/validation';
import {
  getCandidateFields,
  prepareCandidateInitialData,
} from '../jobsPage/config';
import {
  FILTER_MAP,
  getTableHeaders,
  prepareTableData,
  getFilterOptions,
  getHeaderActions,
  filterTagsFromCategories,
  getStagesForFilter,
  getMapedStages,
  getMenuItems,
  prepareJobsInitialData,
  MANAGE_JOBS_FIELDS,
  getJobsForSelectList,
} from './config';

const useStyles = makeStyles(
  ({ palette: { primary }, spacing, breakpoints }) => ({
    form: {
      display: 'grid',
      gridColumnGap: 24,
      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
      gridTemplateAreas: `
      "firstName lastName"
      "professionTitle email"
      "dateOfBirth phone"
      "referral ."
      "cv parseCheckbox"
      "education education"
      "experience experience"
      "skills skills"
      "city address"
      "country ."
    `,
    },
    firstName: {
      gridArea: 'firstName',
    },
    lastName: {
      gridArea: 'lastName',
    },
    email: {
      gridArea: 'email',
    },
    cv: {
      gridArea: 'cv',
    },
    parseCheckbox: {
      gridArea: 'parseCheckbox',
      display: 'flex',
      alignItems: 'center',
    },
    experience: {
      gridArea: 'experience',
    },
    skills: {
      gridArea: 'skills',
    },
    professionTitle: {
      gridArea: 'professionTitle',
    },
    education: {
      gridArea: 'education',
    },
    referral: {
      gridArea: 'referral',
    },
    dateOfBirth: {
      gridArea: 'dateOfBirth',
    },
    phone: {
      gridArea: 'phone',
    },
    address: {
      gridArea: 'address',
    },
    city: {
      gridArea: 'city',
    },
    country: {
      gridArea: 'country',
    },
    subHeader: {
      padding: spacing(8, 0, 10),
      ...sticky(primary.white, 105),
    },
    actionWrapper: {
      display: 'flex',
      alignItems: 'center',
      minHeight: 32,
    },
    spacing: {
      marginLeft: spacing(2),
    },
    search: {
      width: 335,
    },
    table: {
      '& .grid-table-header': {
        ...sticky(primary.bluish8, 235),
      },
      '& .actions-cell': {
        overflow: 'visible',
        padding: spacing(3, 1),
        justifyContent: 'center',
        [breakpoints.up('xLg')]: {
          padding: spacing(3),
        },
      },
    },
    userLabel: {
      fontFamily: 'ProximaNova-Bold',
      fontSize: 16,
      lineHeight: '24px',
    },
    userWrapper: {
      display: 'flex',
      padding: spacing(1.5),
    },
    avatar: {
      width: 'fit-content',
      minWidth: 'fit-content',
    },
    userLabelWrapper: {
      display: 'grid',
      marginLeft: spacing(2),
      width: 'inherit',
      alignItems: 'center',
    },
    customPaper: {
      maxHeight: 202,
      maxWidth: 245,
    },
    jobLabel: {
      color: primary.bluish3,
    },
    listWithTooltip: {
      paddingRight: spacing(8),
    },
    customChip: {
      '&:hover': {
        backgroundColor: primary.bluish7,
      },
    },
  })
);

const CandidatesPage = ({
  location,
  accessControl,
  navigate,
  dispatch,
  ...rest
}) => {
  const classes = useStyles();
  const translations = useTranslations(APP_PAGES.CANDIDATES);
  const [canEdit, canDelete] = hasAuthPermissionsCheck(
    [PERMISSION_CODES.RECRUITMENT_EDIT, PERMISSION_CODES.RECRUITMENT_DELETE],
    accessControl
  );
  const { PAGE, PAGE_SIZE, SEARCH, DEPARTMENT, JOB, STAGE, TAG } = PARAMS;
  const { page: defaultPage, page_size } = CANDIDATES_DEFAULT_PARAMS;
  const { candidates, allJobs } = useSelector(jobsSelector);
  const { departments } = useSelector(divisionsAndDepartmentsSelector);

  const candidateResults = candidates.results;
  const hasCandidates =
    isArray(candidateResults) && !isArrayEmpty(candidateResults);

  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const departmentOptions = getFilterOptions(departments, translations, true);
  const { categories } = useSelector(tagsSelector);
  const [isDeleteCandidateDialogOpened, setIsDeleteCandidateDialogOpened] =
    useState(false);
  const jobOptions = getFilterOptions(allJobs, translations, true);
  const tags = filterTagsFromCategories(categories);
  const tagOptions = getFilterOptions(tags, translations, true);
  const stages = getStagesForFilter(allJobs);
  const { mappedStepNames, mappedStepIds } = getMapedStages(allJobs);

  const stagesOptions = getFilterOptions(stages, translations, true);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [isEditCandidateDrawerOpened, setIsEditCandidateDrawerOpened] =
    useState(false);
  const [isManageJobsDrawerOpened, setIsManageJobsDrawerOpened] =
    useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState({});
  const [selectedFilters, setSelectedFilters] = useState({});
  const jobsForCandidate = selectedCandidate.steps?.map(step => ({
    id: step.job_id,
    title: step.job,
  }));
  const allJobsSelectList = getJobsForSelectList(allJobs);

  const candidateInitialData = prepareCandidateInitialData(selectedCandidate);

  const prepareDetailsUrl = candidateId => `/candidates/${candidateId}`;

  const handlePageChange = ({
    newPage,
    newFilters,
    newSearch,
    isLoadMore,
  } = {}) => {
    const params = {
      [PAGE]: newPage,
      [PAGE_SIZE]: page_size,
      ...(newFilters[DEPARTMENT]
        ? { [DEPARTMENT]: newFilters[DEPARTMENT] }
        : {}),
      ...(newFilters[JOB] ? { [JOB]: newFilters[JOB] } : {}),
      ...(newFilters[STAGE]
        ? {
            [STAGE]: isArray(newFilters[STAGE])
              ? newFilters[STAGE]
              : mappedStepNames?.[mappedStepIds?.[newFilters[STAGE]]],
          }
        : {}),
      ...(newFilters[TAG] ? { [TAG]: newFilters[TAG] } : {}),
      ...(newSearch ? { [SEARCH]: newSearch } : {}),
    };
    const query = parseDuplicateParameters(params);

    if (location.search !== `?${query}`) {
      navigate(`/candidates/?${query}`, { replace: true });
    }
    return getCandidates(dispatch, isLoadMore, params);
  };

  const onSearch = async text => {
    const searchTerm = trimString(text);
    try {
      setPage(defaultPage);
      setSearch(searchTerm);
      setIsLoading(true);
      await handlePageChange({
        newPage: defaultPage,
        newSearch: searchTerm,
        newFilters: selectedFilters,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleLoadMore = () => {
    setPage(page + 1);
    handlePageChange({
      newPage: page + 1,
      newSearch: search,
      newFilters: selectedFilters,
      isLoadMore: true,
    });
  };

  const handleChangeFilter = (filterLabel, value) => {
    const updatedFilters = { ...selectedFilters, [filterLabel]: value };
    setPage(defaultPage);
    setSelectedFilters(updatedFilters);
    handlePageChange({
      newPage: defaultPage,
      newFilters: updatedFilters,
      newSearch: search,
    });
  };

  const handleDepartmentFilter = department => {
    handleChangeFilter(FILTER_MAP.DEPARTMENT, department);
  };

  const handleJobFilter = job => {
    handleChangeFilter(FILTER_MAP.JOB, job);
  };

  const handleStageFilter = stage => {
    handleChangeFilter(FILTER_MAP.STAGE, stage);
  };

  const handleTagFilter = tag => {
    handleChangeFilter(FILTER_MAP.TAG, tag);
  };

  const renderUser = user => {
    return (
      <div className={classes.userWrapper}>
        <UserAvatar
          className={classes.avatar}
          labelClass={classes.userLabel}
          user={user}
          medium
        />
        <div className={classes.userLabelWrapper}>
          <TextBoxWithTooltip
            variant="subtitle1"
            text={getPersonFullName(user)}
          />
          <TextBoxWithTooltip
            className={classes.jobLabel}
            variant="body1"
            text={user.profession_title || '-'}
          />
        </div>
      </div>
    );
  };

  const handleEditCandidate = async candidate => {
    await http.get(api_candidate(candidate.id)).then(({ data }) => {
      setSelectedCandidate(data);
      setIsEditCandidateDrawerOpened(true);
    });
  };

  const handleManageJobs = candidate => {
    setSelectedCandidate(candidate);
    setIsManageJobsDrawerOpened(true);
  };

  const handleCloseManageJobsDrawer = () => {
    setIsManageJobsDrawerOpened(false);
    setSelectedCandidate({});
  };

  const handleSaveJobsForCandidate = async ({ jobs }) => {
    await http
      .patch(api_candidates_in_jobs(selectedCandidate.id), {
        jobs: getObjectToNumberArray(jobs),
      })
      .then(() => {
        handleCloseManageJobsDrawer();
        handlePageChange({
          newPage: page,
          newFilters: selectedFilters,
          newSearch: search,
        });
        showSuccessMessage(translations.successMessages.manageJobsSaved);
      });
  };

  const handleCloseEditCandidatesDrawer = () => {
    setIsEditCandidateDrawerOpened(false);
  };

  const handleSaveCandidate = async candidate => {
    const { cv, ...candidateData } = candidate;
    if (isObject(cv) || !cv) {
      const { parse_cv_data, ...data } = candidateData;
      const formData = new FormData();
      formData.append('cv', cv || '');
      formData.append('parse_cv_data', parse_cv_data || false);

      await http.patch(api_candidate(candidate.id), data).then(() => {
        http.patch(api_candidate(candidate.id), formData).then(() => {
          showSuccessMessage(translations.successMessages.candidateSaved);
          setIsEditCandidateDrawerOpened(false);
          setPage(defaultPage);
          handlePageChange({
            newPage: defaultPage,
            newFilters: selectedFilters,
            newSearch: search,
          });
        });
      });
    } else {
      await http.patch(api_candidate(candidate.id), candidateData).then(() => {
        showSuccessMessage(translations.successMessages.candidateSaved);
        setIsEditCandidateDrawerOpened(false);
        setPage(defaultPage);
        handlePageChange({
          newPage: defaultPage,
          newFilters: selectedFilters,
          newSearch: search,
        });
      });
    }
  };

  const handleDeleteCandidate = candidate => {
    setSelectedCandidate(candidate);
    setIsDeleteCandidateDialogOpened(true);
  };

  const handleCloseDelete = () => {
    setIsDeleteCandidateDialogOpened(false);
  };

  const handleConfirmDelete = async () => {
    try {
      await http.delete(api_candidate(selectedCandidate.id));
      showSuccessMessage(translations.deleteCandidateDialog.deleteSuccess);
      setPage(defaultPage);
      await handlePageChange({
        newPage: defaultPage,
        newFilters: selectedFilters,
        newSearch: search,
      });
    } finally {
      setIsDeleteCandidateDialogOpened(false);
      setIsEditCandidateDrawerOpened(false);
      setSelectedCandidate({});
    }
  };

  const getNotificationMessage = () => {
    return isEmpty(search) || !isObjectEmpty(selectedFilters)
      ? translations.noSearchResultsMessage
      : translations.noResults;
  };

  const handleChipSelect = item => {
    navigate(
      `/jobs/${item.job_id}/details/${item.step_id}/profile/?stats=qualified`
    );
  };
  const listVirtualItemSize = useCallback(
    (index, items) => (index === items.length - 1 ? 28 : 36),
    []
  );

  const getInitialData = async () => {
    try {
      const params = parseQueryParams(
        location.search,
        PAGE_WHITELISTED_PARAMS.CANDIDATES
      );
      if (!isObjectEmpty(params)) {
        const initialSearch = params[SEARCH]?.toString() || '';
        const initialFilters = {
          [DEPARTMENT]: params[DEPARTMENT],
          [JOB]: params[JOB],
          [STAGE]: params[STAGE],
          [TAG]: params[TAG],
        };
        setSearch(initialSearch);
        setSelectedFilters(initialFilters);
        await Promise.all([
          handlePageChange({
            newPage: page,
            newFilters: initialFilters,
            newSearch: initialSearch,
          }),
          getDivisionsAndDepartments(dispatch),
          getTagsCategories(dispatch),
          getAllJobs(dispatch),
        ]);
      } else {
        await Promise.all([
          handlePageChange({
            newPage: page,
            newFilters: selectedFilters,
            newSearch: search,
          }),
          getDivisionsAndDepartments(dispatch),
          getTagsCategories(dispatch),
          getAllJobs(dispatch),
        ]);
      }
    } finally {
      setIsInitialLoad(false);
    }
  };
  const cleanup = useCallback(() => {
    dispatch(clearCandidates());
    dispatch(clearDivisionsAndDepartments());
    dispatch(clearAllJobs());
    dispatch(clearTagsCategories());
  }, [dispatch]);

  useEffect(() => {
    getInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    return cleanup;
  }, [cleanup]);

  const renderActions = user => {
    if (!canEdit) return null;

    return (
      <DotsMenu
        menuItems={getMenuItems(
          translations,
          user,
          handleEditCandidate,
          handleManageJobs
        )}
      />
    );
  };

  const renderList = items => {
    return (
      <div className={classes.listWithTooltip}>
        <ListWithTooltip
          customJobChipClass={classes.customChip}
          moreItemsClass={classes.customChip}
          onChipSelect={handleChipSelect}
          items={items}
          visibleItemsCount={3}
          isJobList
          asyncListProps={{
            position: 'top',
            isDisabled: true,
            shouldDisablePortal: false,
            getItemSize: listVirtualItemSize,
          }}
        />
      </div>
    );
  };

  return (
    <PageContainer
      {...rest}
      location={location}
      translations={translations}
      accessControl={accessControl}
      navigate={navigate}
      dispatch={dispatch}
      shouldPassProps={false}
      isFullWidthContent
    >
      {!isInitialLoad && (
        <div>
          <div className={classes.subHeader}>
            <div className={classes.actionWrapper}>
              <Search
                className={classes.search}
                placeholder={translations.search}
                value={search}
                onChange={onSearch}
              />
              <Filter
                className={classes.spacing}
                customPaperClass={classes.customPaper}
                name={translations.department}
                options={departmentOptions}
                value={selectedFilters[DEPARTMENT]}
                onChange={handleDepartmentFilter}
              />
              <Filter
                className={classes.spacing}
                customPaperClass={classes.customPaper}
                name={translations.job}
                options={jobOptions}
                value={selectedFilters[JOB]}
                onChange={handleJobFilter}
              />
              <Filter
                className={classes.spacing}
                customPaperClass={classes.customPaper}
                name={translations.stage}
                options={stagesOptions}
                value={
                  isArray(selectedFilters[STAGE])
                    ? selectedFilters[STAGE]?.[0]
                    : selectedFilters[STAGE]
                }
                onChange={handleStageFilter}
              />
              <Filter
                className={classes.spacing}
                customPaperClass={classes.customPaper}
                name={translations.tag}
                options={tagOptions}
                value={selectedFilters[TAG]}
                onChange={handleTagFilter}
              />
            </div>
          </div>
          {!isLoading && hasCandidates ? (
            <GridTablePlain
              className={classes.table}
              translations={translations}
              headerActions={
                canEdit ? getHeaderActions(translations.columns) : []
              }
              headers={getTableHeaders(translations.columns)}
              rows={prepareTableData(
                candidateResults,
                renderUser,
                renderActions,
                renderList
              )}
              hasLoadMore={hasNextPage(candidates)}
              prepareDetailsUrl={prepareDetailsUrl}
              onLoadMore={handleLoadMore}
            />
          ) : (
            <NotificationCard content={getNotificationMessage()} />
          )}
        </div>
      )}
      <CustomFormDrawer
        customFormClass={classes.form}
        translations={translations.addCandidatesManually}
        isOpened={isEditCandidateDrawerOpened}
        initialData={candidateInitialData}
        fields={getCandidateFields(classes)}
        onClose={handleCloseEditCandidatesDrawer}
        onSave={handleSaveCandidate}
        onDelete={handleDeleteCandidate}
        hideDelete={!canDelete}
        isInitialValid
        hasCancelButton
        isMiddleWidth
      />
      <CustomFormDrawer
        translations={translations.manageJobs}
        isOpened={isManageJobsDrawerOpened}
        initialData={prepareJobsInitialData(jobsForCandidate)}
        fields={MANAGE_JOBS_FIELDS}
        allJobs={allJobsSelectList}
        onClose={handleCloseManageJobsDrawer}
        onSave={handleSaveJobsForCandidate}
        hideDelete
        hasCancelButton
        isInitialValid
      />
      <AlertDialog
        translations={translations.deleteCandidateDialog}
        isOpened={isDeleteCandidateDialogOpened}
        onClose={handleCloseDelete}
        onConfirm={handleConfirmDelete}
        isWarning
      />
    </PageContainer>
  );
};

CandidatesPage.propTypes = {
  location: PropTypes.object.isRequired,
  accessControl: PropTypes.object.isRequired,
  navigate: PropTypes.func.isRequired,
  dispatch: PropTypes.func.isRequired,
};

export default memo(CandidatesPage);
